/**
 * WorkspaceIntroService.ts — FIRST VISIT EXPERIENCE™ (Phase 7, Build 2.13.0)
 *
 * Tracks which workspace introductions the operator has completed and how
 * often help is opened. Completion is stored locally AND pushed to the cloud
 * via StorageManager — returning operators never see intros twice, even on a
 * new device (hydration restores this record).
 */

import { storageManager } from '@/services/storage/StorageManager';
import type { WorkspaceId } from './helpContent';

export const INTROS_KEY = 'nomad:workspace-intros:v1';

export interface WorkspaceIntroState {
  /** workspaceId → ISO timestamp of "Got It". */
  completed: Record<string, string>;
  /** workspaceId → number of times help (?) was manually opened. */
  helpOpens: Record<string, number>;
  updatedAt: string;
}

function emptyState(): WorkspaceIntroState {
  return { completed: {}, helpOpens: {}, updatedAt: new Date(0).toISOString() };
}

function hasLocalStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

export function loadIntroState(): WorkspaceIntroState {
  if (!hasLocalStorage()) return emptyState();
  try {
    const raw = localStorage.getItem(INTROS_KEY);
    if (!raw) return emptyState();
    const p = JSON.parse(raw) as Partial<WorkspaceIntroState> | null;
    if (!p || typeof p !== 'object') return emptyState();
    const completed: Record<string, string> = {};
    if (p.completed && typeof p.completed === 'object') {
      for (const [k, v] of Object.entries(p.completed)) {
        if (typeof v === 'string') completed[k] = v;
      }
    }
    const helpOpens: Record<string, number> = {};
    if (p.helpOpens && typeof p.helpOpens === 'object') {
      for (const [k, v] of Object.entries(p.helpOpens)) {
        if (typeof v === 'number' && isFinite(v) && v >= 0) helpOpens[k] = Math.floor(v);
      }
    }
    return { completed, helpOpens, updatedAt: typeof p.updatedAt === 'string' ? p.updatedAt : new Date(0).toISOString() };
  } catch {
    return emptyState();
  }
}

function persist(state: WorkspaceIntroState): void {
  state.updatedAt = new Date().toISOString();
  if (hasLocalStorage()) {
    try { localStorage.setItem(INTROS_KEY, JSON.stringify(state)); } catch { /* quota — non-fatal */ }
  }
  storageManager.push('operator', 'workspace-intros', state);
}

export function isIntroCompleted(id: WorkspaceId): boolean {
  return typeof loadIntroState().completed[id] === 'string';
}

export function markIntroCompleted(id: WorkspaceId): void {
  const state = loadIntroState();
  if (state.completed[id]) return; // idempotent — first completion timestamp is preserved
  state.completed[id] = new Date().toISOString();
  persist(state);
}

export function recordHelpOpened(id: WorkspaceId): void {
  const state = loadIntroState();
  state.helpOpens[id] = (state.helpOpens[id] ?? 0) + 1;
  persist(state);
}

export function getCompletedIntroIds(): string[] {
  return Object.keys(loadIntroState().completed);
}

export function getHelpUsage(): Record<string, number> {
  return { ...loadIntroState().helpOpens };
}

/** Dev Center: Reset Introductions — every workspace re-introduces itself. */
export function resetIntros(): void {
  persist(emptyState());
}

/** Dev Center: Replay Introduction — clear a single workspace's completion. */
export function resetIntro(id: WorkspaceId): void {
  const state = loadIntroState();
  delete state.completed[id];
  persist(state);
}

/**
 * Cloud restore (Hydration step) — merge the cloud record into local state so a
 * returning operator on a fresh device never sees completed intros again.
 * Merge rules: completions are a union (earliest timestamp wins — a completion
 * is never un-done by hydration); help-open counts take the max per workspace.
 */
export async function hydrateWorkspaceIntrosFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<Partial<WorkspaceIntroState>>('operator', 'workspace-intros');
  if (!cloud || typeof cloud !== 'object') return false;
  const local = loadIntroState();
  const merged: WorkspaceIntroState = {
    completed: { ...local.completed },
    helpOpens: { ...local.helpOpens },
    updatedAt: local.updatedAt,
  };
  if (cloud.completed && typeof cloud.completed === 'object') {
    for (const [k, v] of Object.entries(cloud.completed)) {
      if (typeof v !== 'string') continue;
      const existing = merged.completed[k];
      merged.completed[k] = existing && existing < v ? existing : v;
    }
  }
  if (cloud.helpOpens && typeof cloud.helpOpens === 'object') {
    for (const [k, v] of Object.entries(cloud.helpOpens)) {
      if (typeof v === 'number' && isFinite(v) && v >= 0) {
        merged.helpOpens[k] = Math.max(merged.helpOpens[k] ?? 0, Math.floor(v));
      }
    }
  }
  merged.updatedAt = new Date().toISOString();
  if (hasLocalStorage()) {
    try { localStorage.setItem(INTROS_KEY, JSON.stringify(merged)); } catch { /* quota — non-fatal */ }
  }
  return true;
}
